import React, { useState, useEffect } from 'react';
import { Story } from '../../types/Story';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
 
 
interface PublishedStory {
    storyId: string;
    title: string;
    type: string;
    authorPseudonym: string;
    authorAvatar: string;
    thumbnail?: any;
    tags?: string[];
    reads: number;
    wordCount?: number;
    reactions: {
        heart: number;
        star: number;
        smile: number;
        party: number;
    };
}
 
interface HomeProps {
    onNavigateHome: () => void | Promise<void>;
    onNavigateStorybook: () => void | Promise<void>;
    onNavigateSignup: () => void | Promise<void>;
    onNavigateChapterBook: () => void | Promise<void>;
    onNavigateGraphicNovel: () => void | Promise<void>;
    onNavigatePictureBook: () => void | Promise<void>;
    onNavigateFeaturedStories: () => void | Promise<void>;
}
 
// ─── Color tokens matching the new design system ───────────────────────────
const C = {
    primary:        '#6a37d4',
    primaryLight:   '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:      '#006947',
    secondaryContainer: '#69f6b8',
    tertiary:       '#6f5900',
    tertiaryContainer: '#fed01b',
    surface:        '#f3f6ff',
    surfaceContainer: '#dce9fd',
    surfaceHigh:    '#d6e3f9',
    surfaceHighest: '#cfdef5',
    surfaceLow:     '#eaf1ff',
    surfaceLowest:  '#ffffff',
    onBackground:   '#252f3c',
    onSurface:      '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant: '#a4aebe',
    error:          '#b41340',
};
 
const Home: React.FC<HomeProps> = ({
    onNavigateHome,
    onNavigateStorybook,
    onNavigateSignup,
    onNavigateChapterBook,
    onNavigateGraphicNovel,
    onNavigatePictureBook,
    onNavigateFeaturedStories
}) => {
    const navigate = useNavigate();
    const [popularStories, setPopularStories] = useState<PublishedStory[]>([]);
    const [storiesLoading, setStoriesLoading] = useState(true);
    const { currentUser } = useAuth();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showParentModal, setShowParentModal] = React.useState(false);
    const [showTeacherModal, setShowTeacherModal] = React.useState(false);
    const [teacherEmail, setTeacherEmail] = React.useState('');
    const [emailStatus, setEmailStatus] = React.useState<'idle' | 'loading' | 'success' | 'error'>('idle');
 
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    useEffect(() => {
        const loadPopularStories = async () => {
            try {
                const storiesRef = collection(db, 'publishedStories');
                const querySnapshot = await getDocs(storiesRef);
                const stories = querySnapshot.docs.map(doc => ({
                    ...doc.data(),
                    storyId: doc.id
                } as PublishedStory));
                const sorted = stories.sort((a, b) => b.reactions.heart - a.reactions.heart);
                setPopularStories(sorted.slice(0, 6));
            } catch (error) {
                console.error('Error loading popular stories:', error);
            } finally {
                setStoriesLoading(false);
            }
        };
        loadPopularStories();
    }, []);
 
    const getBackgroundStyle = (bg: any) => {
        if (!bg) return { background: '#6C5CE7' };
        if (bg.type === 'solid') return { background: bg.value };
        if (bg.type === 'gradient') return { background: bg.value };
        if (bg.type === 'image') return {
            backgroundImage: `url(${bg.value})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        };
        if (bg.type === 'pattern') {
            const patterns: any = {
                'Stars':   { value: 'radial-gradient(circle, #FFD700 1px, transparent 1px)', size: '20px 20px' },
                'Dots':    { value: 'radial-gradient(circle, #4ECDC4 2px, transparent 2px)', size: '30px 30px' },
                'Hearts':  { value: 'repeating-linear-gradient(45deg, #FF6B6B 0px, #FF6B6B 10px, transparent 10px, transparent 20px)', size: 'auto' },
                'Stripes': { value: 'repeating-linear-gradient(0deg, #4ECDC4, #4ECDC4 10px, #E8F9F8 10px, #E8F9F8 20px)', size: 'auto' },
                'Waves':   { value: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #4ECDC4 10px, #4ECDC4 20px)', size: 'auto' },
                'Grid':    { value: 'linear-gradient(#4ECDC4 1px, transparent 1px), linear-gradient(90deg, #4ECDC4 1px, transparent 1px)', size: '20px 20px' }
            };
            const pattern = patterns[bg.value] || patterns['Stars'];
            return { background: bg.color || '#FFFFFF', backgroundImage: pattern.value, backgroundSize: pattern.size };
        }
        return { background: '#6C5CE7' };
    };
 
    const getStoryIcon = (type: string): string => {
        switch(type) {
            case 'regular-book':  return '📖';
            case 'graphic-novel': return '🎨';
            case 'picture-book':  return '📚';
            default:              return '📝';
        }
    };
 
    const handleStartWriting = () => {
        navigate('/regular-book-editor');
    };
 
    const handleTeacherEmailSubmit = async () => {
        if (!teacherEmail || !teacherEmail.includes('@')) {
            setEmailStatus('error');
            return;
        }
        setEmailStatus('loading');
        try {
            await addDoc(collection(db, 'teacherWaitlist'), {
                email: teacherEmail,
                timestamp: Timestamp.now(),
                status: 'pending',
                notified: false
            });
            setEmailStatus('success');
            setTeacherEmail('');
            setTimeout(() => setEmailStatus('idle'), 2000);
        } catch (error) {
            console.error('Error adding to waitlist:', error);
            setEmailStatus('error');
            setTimeout(() => setEmailStatus('idle'), 3000);
        }
    };
 
    // ── Placeholder story cards shown in hero when no real stories yet ──────
    const heroCards = [
        { title: 'The Lost Key',     genre: 'Mystery',   image: '/thelostkey.png',     genreColor: C.primary,   rotate: '-3deg' },
        { title: 'Galaxy Travelers', genre: 'Sci-Fi',    image: '/galaxytravelers.png', genreColor: C.secondary, rotate: '6deg'  },
        { title: 'Shadow Woods',     genre: 'Adventure', image: '/shadowwoods.png',     genreColor: C.tertiary,  rotate: '2deg'  },
    ];
 
    const genreBadgeColor: Record<string, { bg: string; text: string }> = {
        Fantasy:   { bg: C.primary,    text: '#fff' },
        'Sci-Fi':  { bg: C.secondary,  text: '#fff' },
        Adventure: { bg: C.tertiary,   text: '#fff' },
        Mystery:   { bg: C.error,      text: '#fff' },
    };
 
    // ── Shared button style factory ──────────────────────────────────────────
    const pillBtn = (bg: string, color: string, hoverBg: string) => ({
        base: {
            background: bg, color,
            border: 'none',
            borderRadius: '9999px',
            padding: '14px 36px',
            fontSize: '1.05rem',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: "'Epilogue', sans-serif",
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
        } as React.CSSProperties,
        enter: (e: React.MouseEvent<HTMLButtonElement>) => {
            (e.currentTarget as HTMLButtonElement).style.background = hoverBg;
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)';
        },
        leave: (e: React.MouseEvent<HTMLButtonElement>) => {
            (e.currentTarget as HTMLButtonElement).style.background = bg;
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
        },
    });
 
    const primaryBtn  = pillBtn('linear-gradient(135deg, #6a37d4 0%, #ae8dff 100%)', '#fff', 'linear-gradient(135deg, #5a27c0 0%, #9d7df0 100%)');
    const outlineBtn  = pillBtn('transparent', C.primary, C.primaryContainer);
 
    return (
        <div style={{ background: C.surface, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
 
            {/* ═══════════════════════════════════════════════════════════════
                HERO
            ═══════════════════════════════════════════════════════════════ */}
            <section style={{
                background: C.surface,
                padding: isMobile ? '40px 24px 60px' : '64px 48px 80px',
                overflow: 'hidden',
            }}>
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: isMobile ? '48px' : '64px',
                    alignItems: 'center',
                }}>
                    {/* Left: copy */}
                    <div style={{ zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <div style={{
                            display: 'inline-block',
                            background: C.tertiaryContainer,
                            color: C.tertiary,
                            padding: '6px 16px',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: '700',
                            letterSpacing: '1.5px',
                            textTransform: 'uppercase',
                            marginBottom: '20px',
                            fontFamily: "'Epilogue', sans-serif",
                        }}>
                            Welcome to your studio
                        </div>
 
                        <h1 style={{
                            fontFamily: "'Epilogue', sans-serif",
                            fontSize: isMobile ? '2.8rem' : '4.2rem',
                            fontWeight: '900',
                            lineHeight: '1.1',
                            color: C.onBackground,
                            margin: '0 0 20px 0',
                            textAlign: 'left',
                        }}>
                            Ignite Your<br />
                            <span style={{ color: C.primary, fontStyle: 'italic' }}>Narrative</span>
                        </h1>
 
                        <p style={{
                            fontSize: isMobile ? '1rem' : '1.15rem',
                            color: C.onSurfaceVariant,
                            lineHeight: '1.7',
                            maxWidth: '480px',
                            margin: '0 0 36px 0',
                        }}>
                            Plan your story, write every chapter, design your cover, and share it with a safe community built just for young creators.
                        </p>

                        <div style={{
    display: 'inline-block',
    background: `${C.primary}15`,
    color: C.primary,
    padding: '8px 14px',
    borderRadius: '999px',
    fontSize: '0.8rem',
    fontWeight: '600',
    marginBottom: '28px',
}}>
    ✨ Part writing studio, part creative community
</div>
 
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            <button
                                onClick={handleStartWriting}
                                style={primaryBtn.base}
                                onMouseEnter={primaryBtn.enter}
                                onMouseLeave={primaryBtn.leave}
                            >
                                Start Writing Free ⚡
                            </button>
                            <button
                                onClick={() => navigate('/featured-stories')}
                                style={{
                                    ...outlineBtn.base,
                                    border: `2px solid ${C.outlineVariant}`,
                                }}
                                onMouseEnter={outlineBtn.enter}
                                onMouseLeave={outlineBtn.leave}
                            >
                                Explore Stories
                            </button>
                        </div>
 
                        {/* For Parents / For Teachers — ghost chips */}
                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                            {[
                                { label: '👨‍👩‍👧 For Parents', action: () => setShowParentModal(true) },
                                { label: '👩‍🏫 For Teachers', action: () => setShowTeacherModal(true) },
                            ].map(({ label, action }) => (
                                <button
                                    key={label}
                                    onClick={action}
                                    style={{
                                        background: 'transparent',
                                        border: `1.5px solid ${C.outlineVariant}`,
                                        borderRadius: '9999px',
                                        padding: '7px 18px',
                                        fontSize: '0.82rem',
                                        fontWeight: '600',
                                        color: C.onSurfaceVariant,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.borderColor = C.primary;
                                        e.currentTarget.style.color = C.primary;
                                        e.currentTarget.style.background = C.primaryContainer;
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.borderColor = C.outlineVariant;
                                        e.currentTarget.style.color = C.onSurfaceVariant;
                                        e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
 
                    {/* Right: tilted story cards */}
                    {!isMobile && (
                        <div style={{ position: 'relative', minHeight: '480px' }}>
                            {/* Soft glow blobs */}
                            <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '260px', height: '260px', background: `${C.secondaryContainer}40`, borderRadius: '50%', filter: 'blur(60px)', zIndex: 0 }} />
                            <div style={{ position: 'absolute', bottom: '-30px', left: '-20px', width: '180px', height: '180px', background: `${C.primaryContainer}50`, borderRadius: '50%', filter: 'blur(50px)', zIndex: 0 }} />
 
                            <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                {/* Column 1 — offset downward */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '48px' }}>
                                    {[heroCards[0], heroCards[1]].map((card, i) => (
                                        <HeroCard key={i} card={card} />
                                    ))}
                                </div>
                                {/* Column 2 */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <HeroCard card={heroCards[2]} tall />
                                    {/* Writing tip chip */}
                                    <div style={{
                                        background: 'rgba(255,255,255,0.85)',
                                        backdropFilter: 'blur(24px)',
                                        borderRadius: '16px',
                                        padding: '16px',
                                        border: `1px solid ${C.surfaceHigh}`,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                    }}>
                                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: C.secondaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>✨</div>
                                        <div>
                                            <div style={{ fontWeight: '700', fontSize: '0.85rem', color: C.onBackground }}>Magic Suggestion</div>
                                            <div style={{ fontSize: '0.75rem', color: C.onSurfaceVariant }}>Try: "The dragon sneezed…"</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
 
            {/* ═══════════════════════════════════════════════════════════════
                HOW IT WORKS  (replaces Tools + Why Scriblish)
            ═══════════════════════════════════════════════════════════════ */}
            <section style={{ background: C.surfaceLow, padding: isMobile ? '60px 24px' : '80px 80px', borderRadius: '3rem 3rem 0 0', marginTop: '-16px' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '56px' }}>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 12px 0' }}>
                            Everything you need to write your best story
                        </h2>
                        <p style={{ color: C.onSurfaceVariant, fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto', lineHeight: '1.6' }}>
                            Scriblish guides you from blank page to published author — with tools that make writing feel like play.
                        </p>
                    </div>
 
                    {/* 2×2 feature grid — wider on desktop */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                        {[
                            {
                                emoji: '🎭',
                                title: 'Build your world',
                                desc: 'Design characters, map your plot, and plan every twist with the Story Planner and Character Builder before you write a single word.',
                                bg: C.primaryContainer,
                                accent: C.primary,
                            },
                            {
                                emoji: '✍️',
                                title: 'Write chapter by chapter',
                                desc: 'The rich-text editor keeps your chapters organised and your ideas flowing. Switch between chapters without losing your place.',
                                bg: `${C.secondaryContainer}60`,
                                accent: C.secondary,
                            },
                            {
                                emoji: '🎨',
                                title: 'Design a cover you love',
                                desc: 'Add stickers, backgrounds, and your title to create a book cover that looks like it belongs on a real shelf.',
                                bg: `${C.tertiaryContainer}80`,
                                accent: C.tertiary,
                            },
                            {
                                emoji: '🌍',
                                title: 'Publish and earn badges',
                                desc: 'Share your story with the Scriblish community, collect reactions, hit milestones, and unlock writing achievements.',
                                bg: C.surfaceContainer,
                                accent: C.primary,
                            },
                        ].map((feat, i) => (
                            <div key={i} style={{
                                background: feat.bg,
                                borderRadius: '20px',
                                padding: isMobile ? '28px 24px' : '36px 32px',
                                display: 'flex',
                                gap: '20px',
                                alignItems: 'flex-start',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                cursor: 'default',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                                <div style={{ fontSize: '2rem', flexShrink: 0, marginTop: '2px' }}>{feat.emoji}</div>
                                <div>
                                    <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1.1rem', color: feat.accent, margin: '0 0 8px 0' }}>
                                        {feat.title}
                                    </h3>
                                    <p style={{ color: C.onSurfaceVariant, fontSize: '0.95rem', lineHeight: '1.6', margin: 0 }}>
                                        {feat.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
 
            {/* ═══════════════════════════════════════════════════════════════
                WEEKLY PROMPTS
            ═══════════════════════════════════════════════════════════════ */}
            <section style={{ background: C.surfaceContainer, padding: isMobile ? '60px 24px' : '80px 80px' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    {/* Header row */}
                    <div style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'flex-end',
                        gap: '20px',
                        marginBottom: '48px',
                    }}>
                        <div style={{ maxWidth: '560px' }}>
                            <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 12px 0' }}>
                                Weekly Prompts
                            </h2>
                            <p style={{ color: C.onSurfaceVariant, fontSize: '1.05rem', lineHeight: '1.6', margin: 0 }}>
                                Every Monday we drop new sparks to set your imagination on fire. What will you build today?
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/prompts')}
                            style={{
                                background: C.secondaryContainer,
                                color: C.secondary,
                                border: 'none',
                                borderRadius: '10px',
                                padding: '12px 24px',
                                fontSize: '0.95rem',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s',
                                fontFamily: "'Epilogue', sans-serif",
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = C.secondary; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = C.secondaryContainer; e.currentTarget.style.color = C.secondary; }}
                        >
                            View All Prompts →
                        </button>
                    </div>
 
                    {/* Prompt cards grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                        gap: '24px',
                    }}>
                        {[
                            {
                                icon: '🗝️',
                                iconBg: C.primaryContainer,
                                title: 'The Lost Key',
                                body: 'You find a rusty key under your pillow. It doesn\'t open any door in your house, but it starts a sound coming from the attic…',
                                linkColor: C.primary,
                            },
                            {
                                icon: '🚀',
                                iconBg: C.secondaryContainer,
                                title: 'Galaxy Travelers',
                                body: 'Your school bus accidentally takes a wrong turn at Jupiter. You have 24 hours before the first bell rings back on Earth.',
                                linkColor: C.secondary,
                            },
                            {
                                icon: '🐾',
                                iconBg: C.tertiaryContainer,
                                title: 'The Talking Pug',
                                body: 'Your dog suddenly starts giving you investment advice. He says the treats market is about to explode!',
                                linkColor: C.tertiary,
                            },
                        ].map((prompt, i) => (
                            <div
                                key={i}
                                style={{
                                    background: C.surfaceLowest,
                                    borderRadius: '24px',
                                    padding: isMobile ? '28px 24px' : '36px 32px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    transition: 'box-shadow 0.25s, transform 0.25s',
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                onClick={handleStartWriting}
                            >
                                {/* Icon badge */}
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '16px',
                                    background: prompt.iconBg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.6rem',
                                    marginBottom: '24px',
                                    transition: 'transform 0.25s',
                                }}>
                                    {prompt.icon}
                                </div>
 
                                <h3 style={{
                                    fontFamily: "'Epilogue', sans-serif",
                                    fontSize: '1.3rem',
                                    fontWeight: '800',
                                    color: C.onBackground,
                                    margin: '0 0 12px 0',
                                }}>
                                    {prompt.title}
                                </h3>
 
                                <p style={{
                                    color: C.onSurfaceVariant,
                                    fontSize: '0.95rem',
                                    lineHeight: '1.65',
                                    margin: '0 0 28px 0',
                                    flexGrow: 1,
                                }}>
                                    {prompt.body}
                                </p>
 
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    color: prompt.linkColor,
                                    fontWeight: '700',
                                    fontSize: '0.95rem',
                                    fontFamily: "'Epilogue', sans-serif",
                                }}>
                                    Write this Story ↗
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
 
            {/* ═══════════════════════════════════════════════════════════════
                YOUNG AUTHORS SHOWCASE  (horizontal carousel)
            ═══════════════════════════════════════════════════════════════ */}
            <section style={{ padding: isMobile ? '60px 0' : '80px 0', background: C.surfaceLowest, overflow: 'hidden' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '0 24px' : '0 80px' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: '900', color: C.onBackground, textAlign: 'center', margin: '0 0 12px 0' }}>
                        Young Authors Showcase
                    </h2>
                    <p style={{ textAlign: 'center', color: C.onSurfaceVariant, fontSize: '1.05rem', margin: '0 0 48px 0' }}>
                        See what the community is creating right now.
                    </p>
                </div>
 
                {storiesLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px', fontSize: '2rem' }}>Loading stories... 📚</div>
                ) : popularStories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 24px', maxWidth: '400px', margin: '0 auto' }}>
                        <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📚</div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.4rem', fontWeight: '800', margin: '0 0 8px 0', color: C.onBackground }}>No stories yet!</h3>
                        <p style={{ color: C.onSurfaceVariant, margin: 0 }}>Be the first to publish and share your story!</p>
                    </div>
                ) : (
                    <>
                        {isMobile ? (
                            /* ── Mobile: touch-swipeable carousel ── */
                            <div className="scriblish-carousel" style={{
                                display: 'flex',
                                gap: '16px',
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                scrollSnapType: 'x mandatory',
                                paddingLeft: '24px',
                                paddingRight: '24px',
                                paddingBottom: '12px',
                                touchAction: 'pan-x',
                            }}>
                                {popularStories.slice(0, 6).map((story) => {
                                    const coverData = story.thumbnail && typeof story.thumbnail === 'string'
                                        ? JSON.parse(story.thumbnail) : story.thumbnail;
                                    return (
                                        <div key={story.storyId} style={{ flexShrink: 0, width: '75vw', maxWidth: '280px', scrollSnapAlign: 'start' }}>
                                            <ShowcaseCard
                                                story={story}
                                                coverData={coverData}
                                                getBackgroundStyle={getBackgroundStyle}
                                                getStoryIcon={getStoryIcon}
                                                onClick={() => navigate(`/read-story/${story.storyId}`, { state: { story } })}
                                                genreBadgeColor={genreBadgeColor}
                                                C={C}
                                                fullWidth
                                            />
                                        </div>
                                    );
                                })}
                                {/* Trailing spacer so last card doesn't clip */}
                                <div style={{ flexShrink: 0, width: '8px' }} />
                            </div>
                        ) : (
                            /* ── Desktop: balanced 3-column grid ── */
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '28px',
                                maxWidth: '1400px',
                                margin: '0 auto',
                                padding: '0 80px',
                            }}>
                                {popularStories.slice(0, 3).map((story) => {
                                    const coverData = story.thumbnail && typeof story.thumbnail === 'string'
                                        ? JSON.parse(story.thumbnail) : story.thumbnail;
                                    return (
                                        <ShowcaseCard
                                            key={story.storyId}
                                            story={story}
                                            coverData={coverData}
                                            getBackgroundStyle={getBackgroundStyle}
                                            getStoryIcon={getStoryIcon}
                                            onClick={() => navigate(`/read-story/${story.storyId}`, { state: { story } })}
                                            genreBadgeColor={genreBadgeColor}
                                            C={C}
                                            fullWidth
                                        />
                                    );
                                })}
                            </div>
                        )}
 
                        <div style={{ textAlign: 'center', marginTop: '48px' }}>
                            <button
                                onClick={() => navigate('/featured-stories')}
                                style={primaryBtn.base}
                                onMouseEnter={primaryBtn.enter}
                                onMouseLeave={primaryBtn.leave}
                            >
                                View All Stories →
                            </button>
                        </div>
                    </>
                )}
            </section>
 
            {/* ═══════════════════════════════════════════════════════════════
                SAFETY SECTION
            ═══════════════════════════════════════════════════════════════ */}
            <section style={{
                background: C.surfaceHighest,
                padding: isMobile ? '60px 24px' : '80px 48px',
                borderRadius: '3rem',
                margin: isMobile ? '0' : '0 24px',
            }}>
                <div style={{
                    maxWidth: '1100px',
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '64px',
                    alignItems: 'center',
                }}>
                    {/* Left: text */}
                    <div>
                        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '24px' }}>
                            <div style={{ position: 'absolute', top: '-12px', left: '-12px', width: '72px', height: '72px', background: `${C.primary}15`, borderRadius: '50%' }} />
                            <span style={{ fontSize: '4.5rem', position: 'relative', zIndex: 1 }}>🛡️</span>
                        </div>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '2rem' : '2.6rem', fontWeight: '900', color: C.onBackground, margin: '0 0 16px 0' }}>
                            A Supportive Space for Big Ideas
                        </h2>
                        <p style={{ color: C.onSurfaceVariant, fontSize: '1.05rem', lineHeight: '1.7', margin: '0 0 32px 0' }}>
                            Great writing happens when you feel safe to explore. Scriblish is built with tweens in mind.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {[
                                { icon: '✅', title: 'Human Moderation',  desc: 'Every story is reviewed to keep things kind and constructive.' },
                                { icon: '🔒', title: 'Privacy First',      desc: 'No real names, no tracking. Just your pen name and imagination.' },
                                { icon: '👨‍👩‍👧', title: 'Parent Dashboard', desc: 'Parents can see progress and celebrate achievements together.' },
                            ].map((item, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: C.secondaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
                                        {item.icon}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '700', color: C.onBackground, marginBottom: '4px', fontFamily: "'Epilogue', sans-serif" }}>{item.title}</div>
                                        <div style={{ color: C.onSurfaceVariant, fontSize: '0.95rem', lineHeight: '1.5' }}>{item.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
 
                    {/* Right: Writing tip card */}
                    <div style={{
                        background: C.surfaceLowest,
                        borderRadius: '2rem',
                        padding: '48px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                        position: 'relative',
                        overflow: 'hidden',
                    }}>
                        <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: `${C.secondaryContainer}30`, borderRadius: '0 0 0 100%' }} />
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '28px' }}>
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ff5f57' }} />
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#febc2e' }} />
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#28c840' }} />
                        </div>
                        <p style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1.2rem', color: C.primary, fontStyle: 'italic', marginBottom: '20px' }}>
                            Writing Tip of the Day
                        </p>
                        <p style={{ fontSize: '1.15rem', lineHeight: '1.7', color: C.onSurface, margin: '0 0 32px 0' }}>
                            "Show, don't tell! Instead of saying 'He was scared', describe how his knees felt like jelly and the air turned cold."
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: C.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>
                                💡
                            </div>
                            <div style={{ fontWeight: '700', color: C.onBackground, fontFamily: "'Epilogue', sans-serif" }}>Sparky the Muse</div>
                        </div>
                    </div>
                </div>
            </section>
 
            {/* ═══════════════════════════════════════════════════════════════
                FINAL CTA
            ═══════════════════════════════════════════════════════════════ */}
            <section style={{ padding: isMobile ? '60px 24px' : '80px 48px', textAlign: 'center' }}>
                <div style={{ maxWidth: '640px', margin: '0 auto' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '2rem' : '3rem', fontWeight: '900', color: C.onBackground, margin: '0 0 16px 0' }}>
                        Ready to write your first chapter?
                    </h2>
                    <p style={{ fontSize: '1.1rem', color: C.onSurfaceVariant, margin: '0 0 40px 0', lineHeight: '1.6' }}>
                        Join thousands of young authors already making magic with their words.
                    </p>
                    <button
                        onClick={handleStartWriting}
                        style={{ ...primaryBtn.base, fontSize: '1.2rem', padding: '18px 52px', boxShadow: `0 12px 40px ${C.primary}35` }}
                        onMouseEnter={primaryBtn.enter}
                        onMouseLeave={primaryBtn.leave}
                    >
                        Get Started for Free ⚡
                    </button>
                </div>
            </section>
 
            {/* ═══════════════════════════════════════════════════════════════
                PARENT MODAL  (unchanged logic, updated fonts)
            ═══════════════════════════════════════════════════════════════ */}
            {showParentModal && (
                <Modal onClose={() => setShowParentModal(false)} isMobile={isMobile} C={C}>
                    <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '12px' }}>👨‍👩‍👧‍👦</div>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>Information for Parents</h2>
                    </div>
 
                    <ModalSection icon="✨" title="Welcome!" C={C}>
                        <p style={{ fontSize: '0.95rem', lineHeight: '1.6', color: '#555', margin: 0 }}>
                            <strong>Scriblish</strong> is a creative writing platform designed to help children build strong literacy skills through storytelling, chapter books, and fun writing challenges. Whether your child loves reading, wants to become an author, or needs extra writing practice, this space helps them grow confidently in a fun, age-appropriate environment.
                        </p>
                    </ModalSection>
 
                    <ModalSection icon="📚" title="Educational Benefits" C={C}>
                        <p style={{ fontSize: '0.95rem', lineHeight: '1.6', color: '#555', marginBottom: '16px' }}>
                            Our writing studio includes guided planners, chapter organization tools, and prompts that align with foundational literacy skills — including plot development, descriptive writing, and sentence fluency.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                            {['Improves reading comprehension','Builds writing stamina','Teaches story structure','Expands vocabulary','Encourages creativity','Develops self-expression'].map((b, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#555' }}>
                                    <span style={{ color: C.primary }}>✓</span> {b}
                                </div>
                            ))}
                        </div>
                    </ModalSection>
 
                    <ModalSection icon="🎨" title="How Kids Use the Platform" C={C}>
                        <ul style={{ fontSize: '0.9rem', lineHeight: '1.9', color: '#555', paddingLeft: '20px', margin: 0 }}>
                            <li>Write and publish their own chapter books</li>
                            <li>Design book covers using safe, app-approved graphics</li>
                            <li>Collect badges for writing milestones</li>
                            <li>Read stories written by other kids</li>
                            <li>Engage in safe writing challenges and contests</li>
                        </ul>
                    </ModalSection>
 
                    <div style={{ background: '#FFF9E6', padding: '24px', borderRadius: '12px', border: '2px solid #FFD93D', marginBottom: '24px' }}>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.1rem', fontWeight: '800', color: C.onBackground, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            🛡️ Safety & Moderation
                        </h3>
                        {['All published content is moderated for age-appropriateness','No full names, personal info, or unsafe content is allowed','Kids can write privately or share only when ready','Reporting features and review systems are built-in','No outside messaging or unsafe interactions'].map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.9rem', color: '#555', marginBottom: '8px' }}>
                                <span style={{ color: C.primary, flexShrink: 0 }}>✓</span> {item}
                            </div>
                        ))}
                    </div>
 
                    <div style={{ textAlign: 'center' }}>
                        <button onClick={() => setShowParentModal(false)} style={{ ...primaryBtn.base, margin: '0 auto' }} onMouseEnter={primaryBtn.enter} onMouseLeave={primaryBtn.leave}>
                            Got it, thanks!
                        </button>
                    </div>
                </Modal>
            )}
 
            {/* ═══════════════════════════════════════════════════════════════
                TEACHER MODAL  (unchanged logic, updated fonts)
            ═══════════════════════════════════════════════════════════════ */}
            {showTeacherModal && (
                <Modal onClose={() => setShowTeacherModal(false)} isMobile={isMobile} C={C}>
                    <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '12px' }}>👩‍🏫</div>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 4px 0' }}>Teacher Dashboard</h2>
                        <p style={{ color: C.onSurfaceVariant, margin: 0, fontSize: '0.95rem' }}>Bringing Scriblish to Your Classroom</p>
                    </div>
 
                    <ModalSection icon="✨" title="What You'll Get" C={C}>
                        <p style={{ fontSize: '0.95rem', lineHeight: '1.6', color: '#555', marginBottom: '16px' }}>
                            We're building powerful tools to help educators integrate creative writing into their curriculum. Here's what's on the way:
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {[
                                { icon: '👥', title: 'Classroom Groups',   desc: 'Create and manage multiple classes with student rosters' },
                                { icon: '📊', title: 'Progress Tracking',  desc: 'Monitor student writing activity, word counts, and milestones' },
                                { icon: '🎯', title: 'Safe Sharing',       desc: 'Students can share stories within the class for peer review' },
                                { icon: '🏆', title: 'Class Leaderboards', desc: 'Motivate students with friendly competition and achievements' },
                            ].map((f, i) => (
                                <div key={i} style={{ display: 'flex', gap: '14px', padding: '14px', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef' }}>
                                    <div style={{ fontSize: '1.8rem', flexShrink: 0 }}>{f.icon}</div>
                                    <div>
                                        <div style={{ fontWeight: '700', color: C.onBackground, marginBottom: '2px', fontSize: '0.95rem', fontFamily: "'Epilogue', sans-serif" }}>{f.title}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#666', lineHeight: '1.4' }}>{f.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </ModalSection>
 
                    {/* Notify me */}
                    <div style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)', padding: '24px', borderRadius: '12px', border: '2px solid #C7D2FE', marginBottom: '24px' }}>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.05rem', fontWeight: '800', color: C.onBackground, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            📧 Get Notified When We Launch
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '14px', lineHeight: '1.5' }}>
                            Be among the first educators to bring Scriblish to your classroom. We'll send you early access and special educator pricing.
                        </p>
                        {emailStatus === 'success' ? (
                            <div style={{ background: '#D1FAE5', color: '#065F46', padding: '14px', borderRadius: '8px', textAlign: 'center', fontWeight: '600', border: '2px solid #34D399' }}>
                                ✓ Thanks! We'll notify you when we launch.
                            </div>
                        ) : (
                            <>
                                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px' }}>
                                    <input
                                        type="email"
                                        placeholder="Enter your school email"
                                        value={teacherEmail}
                                        onChange={(e) => setTeacherEmail(e.target.value)}
                                        onKeyPress={(e) => { if (e.key === 'Enter') handleTeacherEmailSubmit(); }}
                                        disabled={emailStatus === 'loading'}
                                        style={{
                                            flex: 1, padding: '12px 16px', borderRadius: '8px',
                                            border: emailStatus === 'error' ? '2px solid #F87171' : '2px solid #C7D2FE',
                                            fontSize: '0.95rem', outline: 'none',
                                            opacity: emailStatus === 'loading' ? 0.6 : 1,
                                        }}
                                    />
                                    <button
                                        onClick={handleTeacherEmailSubmit}
                                        disabled={emailStatus === 'loading'}
                                        style={{ ...primaryBtn.base, padding: '12px 24px', borderRadius: '8px', background: emailStatus === 'loading' ? '#9CA3AF' : C.primary, cursor: emailStatus === 'loading' ? 'not-allowed' : 'pointer' }}
                                    >
                                        {emailStatus === 'loading' ? 'Saving…' : 'Notify Me'}
                                    </button>
                                </div>
                                {emailStatus === 'error' && <div style={{ marginTop: '8px', color: '#DC2626', fontSize: '0.82rem', fontWeight: '600' }}>Please enter a valid email address</div>}
                            </>
                        )}
                    </div>
 
                    <div style={{ textAlign: 'center' }}>
                        <button
                            onClick={() => setShowTeacherModal(false)}
                            style={{ ...outlineBtn.base, border: `2px solid ${C.primary}`, margin: '0 auto' }}
                            onMouseEnter={outlineBtn.enter}
                            onMouseLeave={outlineBtn.leave}
                        >
                            Close
                        </button>
                    </div>
                </Modal>
            )}
 
            {/* hide scrollbar for the carousel */}
            <style>{`
                /* Hide scrollbar on the mobile carousel */
                .scriblish-carousel::-webkit-scrollbar { display: none; }
                .scriblish-carousel { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
};
 
// ─────────────────────────────────────────────────────────────────────────────
// Sub-components (same file, no prop-drilling issues)
// ─────────────────────────────────────────────────────────────────────────────
 
const HeroCard: React.FC<{ card: any; tall?: boolean }> = ({ card, tall }) => (
    <div
        onMouseEnter={e => { e.currentTarget.style.transform = 'rotate(0deg) translateY(-6px)'; e.currentTarget.style.boxShadow = '0 20px 48px rgba(0,0,0,0.22)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = `rotate(${card.rotate})`; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.14)'; }}
        style={{
            borderRadius: '20px',
            overflow: 'hidden',
            transform: `rotate(${card.rotate})`,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
            minHeight: tall ? '260px' : '180px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            cursor: 'pointer',
            position: 'relative',
            backgroundImage: `url(${card.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
        }}
    >
        {/* Dark gradient overlay so text is always readable */}
        <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)',
            borderRadius: '20px',
        }} />
        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1, padding: '16px' }}>
            <span style={{
                display: 'inline-block',
                background: card.genreColor,
                color: '#fff',
                padding: '3px 10px',
                borderRadius: '9999px',
                fontSize: '0.65rem',
                fontWeight: '800',
                fontFamily: "'Epilogue', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '7px',
            }}>
                {card.genre}
            </span>
            <p style={{
                fontFamily: "'Epilogue', sans-serif",
                fontWeight: '800',
                color: '#fff',
                margin: 0,
                fontSize: '0.95rem',
                textShadow: '0 1px 4px rgba(0,0,0,0.4)',
                lineHeight: 1.2,
            }}>
                {card.title}
            </p>
        </div>
    </div>
);
 
const ShowcaseCard: React.FC<{
    story: PublishedStory;
    coverData: any;
    getBackgroundStyle: (bg: any) => React.CSSProperties;
    getStoryIcon: (type: string) => string;
    onClick: () => void;
    genreBadgeColor: Record<string, { bg: string; text: string }>;
    C: Record<string, string>;
    fullWidth?: boolean;
}> = ({ story, coverData, getBackgroundStyle, getStoryIcon, onClick, C, fullWidth }) => (
    <div
        onClick={onClick}
        style={{
            width: fullWidth ? '100%' : '220px',
            flexShrink: fullWidth ? undefined : 0,
            background: '#fff',
            borderRadius: '20px',
            overflow: 'hidden',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            border: `1px solid ${C.surfaceHigh}`,
            transition: 'transform 0.25s, box-shadow 0.25s',
            scrollSnapAlign: fullWidth ? undefined : 'start',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.13)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
    >
        {/* Cover — portrait 3:4 ratio */}
        <div style={{
            ...getBackgroundStyle(coverData?.background),
            width: '100%',
            aspectRatio: '3 / 4',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Heart badge */}
            <div style={{
                position: 'absolute', top: '8px', right: '8px', zIndex: 5,
                background: 'rgba(180, 19, 64, 0.92)',
                color: '#fff', padding: '4px 10px', borderRadius: '9999px',
                fontSize: '0.72rem', fontWeight: '700', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', gap: '4px',
            }}>
                ❤️ {story.reactions.heart}
            </div>
 
            {coverData?.elements ? (
                coverData.elements.map((el: any) => {
                    if (el.type === 'graphic' && el.imageUrl) {
                        return (
                            <div key={el.id} style={{
                                position: 'absolute',
                                left: `${el.x}%`,
                                top: `${el.y}%`,
                                transform: `translate(-50%, -50%) rotate(${el.rotation || 0}deg) scale(${el.scale || 1})`,
                                pointerEvents: 'none',
                            }}>
                                <img src={el.imageUrl} alt={el.content} style={{
                                    maxWidth: '120px', maxHeight: '120px',
                                    width: 'auto', height: 'auto', display: 'block',
                                    filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))',
                                }} />
                            </div>
                        );
                    }
                    if (el.type === 'icon') {
                        const iconMap: Record<string, string> = { heart:'♥', star:'★', sparkles:'✨', crown:'👑', wand:'🪄', castle:'🏰', rocket:'🚀', zap:'⚡', shield:'🛡️', cloud:'☁️', sun:'☀️', moon:'🌙', tree:'🌲', flower:'🌸', rainbow:'🌈', gift:'🎁', music:'🎵', book:'📖', palette:'🎨', pencil:'✏️', award:'🏆' };
                        return (
                            <div key={el.id} style={{
                                position: 'absolute',
                                left: `${el.x}%`, top: `${el.y}%`,
                                transform: `translate(-50%, -50%) rotate(${el.rotation || 0}deg) scale(${el.scale || 1})`,
                                fontSize: `${(el.fontSize || 48) * 0.85}px`,
                                color: el.color || '#6C5CE7',
                                pointerEvents: 'none',
                            }}>
                                {iconMap[el.iconName] || '✦'}
                            </div>
                        );
                    }
                    // text elements
                    return (
                        <div key={el.id} style={{
                            position: 'absolute',
                            left: `${el.x}%`, top: `${el.y}%`,
                            transform: 'translate(-50%, -50%)',
                            fontSize: `${(el.fontSize || 16) * 0.85}px`,
                            fontFamily: el.fontFamily || 'Inter',
                            fontWeight: '800',
                            color: el.color || '#FFFFFF',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.35)',
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                            maxWidth: '90%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                            {el.content}
                        </div>
                    );
                })
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '3.5rem' }}>
                    {getStoryIcon(story.type || 'regular-book')}
                </div>
            )}
        </div>
 
        {/* Info panel */}
        <div style={{ padding: '14px 16px 16px' }}>
            <h4 style={{
                fontFamily: "'Epilogue', sans-serif",
                fontSize: '0.95rem', fontWeight: '800',
                color: C.onBackground, margin: '0 0 4px 0',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                {story.title}
            </h4>
            <p style={{ color: C.onSurfaceVariant, margin: '0 0 10px 0', fontSize: '0.8rem' }}>
                {story.authorAvatar} {story.authorPseudonym}
            </p>
 
            {story.tags && story.tags.length > 0 && (
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {story.tags.slice(0, 2).map((tag, i) => (
                        <span key={i} style={{
                            background: C.primaryContainer, color: C.primary,
                            padding: '2px 9px', borderRadius: '9999px',
                            fontSize: '0.68rem', fontWeight: '600',
                        }}>
                            {tag}
                        </span>
                    ))}
                    {story.tags.length > 2 && (
                        <span style={{ color: C.onSurfaceVariant, fontSize: '0.68rem', padding: '2px 0' }}>+{story.tags.length - 2}</span>
                    )}
                </div>
            )}
 
            {story.wordCount && (
                <div style={{ fontSize: '0.75rem', color: C.onSurfaceVariant }}>
                    📝 {story.wordCount.toLocaleString()} words
                </div>
            )}
        </div>
    </div>
);
 
// Reusable modal wrapper
const Modal: React.FC<{ onClose: () => void; isMobile: boolean; C: Record<string, string>; children: React.ReactNode }> = ({ onClose, isMobile, C, children }) => (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '24px', maxWidth: '680px', width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative', boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }}>
            <button onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', background: '#f5f5f5', border: 'none', borderRadius: '50%', width: '36px', height: '36px', fontSize: '1.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', zIndex: 10, transition: 'background 0.2s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = C.primaryContainer; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#f5f5f5'; }}
            >×</button>
            <div style={{ padding: isMobile ? '28px 20px' : '44px 52px' }}>
                {children}
            </div>
        </div>
    </div>
);
 
// Reusable modal section header
const ModalSection: React.FC<{ icon: string; title: string; C: Record<string, string>; children: React.ReactNode }> = ({ icon, title, C, children }) => (
    <div style={{ marginBottom: '28px' }}>
        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.1rem', fontWeight: '800', color: C.primary, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {icon} {title}
        </h3>
        {children}
    </div>
);
 
export default Home;
